/**
 * Responsibility: put a card into sub-collections, from anywhere it is shown.
 *
 * Opened two ways and the same either way: straight after a card is liked,
 * so the grouping is chosen while the card is still in mind, and from a
 * button in the card dialog for a card kept earlier.
 *
 * **It likes the card first when it has to.** Sub-collections attach to a
 * kept card - the hub answers "Not a kept card" with a 404 otherwise - so
 * filing a card that has not been liked has to like it. That is the right
 * meaning rather than a workaround: putting a card in a run you are
 * completing is a stronger statement than liking it, not a weaker one, and
 * a grouping full of cards that vanish from the shelf would be worse than
 * useless.
 *
 * **Nothing is written until Save.** The other pickers write on every chip
 * click because what they are tagging already exists and each click stands
 * alone. Here the person is mid-thought, often on three groupings at once,
 * and a write per tick would be three round trips against a quota for one
 * decision.
 */
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { hubApi } from '../../api/client';
import { useTags } from '../../api/hooks/useTags';
import { TagCheckList } from './TagCheckList';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { marketKeys } from '../../api/hooks/useMarket';

export type TaggableCard = {
  key: string;
  name: string;
  /** Which sub-collections it is in already, when it is a kept card. */
  tags?: string[];
  /** False when it has not been liked yet, so saving must like it first. */
  kept?: boolean;
};

type Props = {
  card: TaggableCard | null;
  onClose: () => void;
  /** Wording for the case where the card was just liked. */
  justLiked?: boolean;
};

export function CardTagDialog({ card, onClose, justLiked = false }: Props) {
  const tags = useTags();
  const client = useQueryClient();
  const [chosen, setChosen] = useState<string[]>([]);

  // Re-seed whenever a different card opens the dialog, so it never shows
  // the previous card's groupings ticked.
  useEffect(() => {
    setChosen(card?.tags ?? []);
  }, [card?.key, card?.tags]);

  const save = useMutation({
    mutationFn: async ({ key, kept }: { key: string; kept: boolean }) => {
      if (!kept) await hubApi.likeCard(key);
      await hubApi.setLikeTags(key, chosen);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['saved'] }),
        client.invalidateQueries({ queryKey: ['collection', 'tags'] }),
        client.invalidateQueries({ queryKey: marketKeys.collection() }),
      ]);
      onClose();
    },
  });

  const available = tags.data ?? [];
  // Nothing to choose from is not worth a dialog, and after a like it would
  // be an interruption that asks nothing.
  if (!card || available.length === 0) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {justLiked ? 'Kept. Add it to a sub-collection?' : 'Sub-collections'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {card.name}
          </Typography>

          {save.error ? (
            <Alert severity="error">{(save.error as Error).message}</Alert>
          ) : null}

          <TagCheckList
            tags={available}
            value={chosen}
            onChange={setChosen}
            label="Which runs does this belong to?"
          />

          {card.kept === false ? (
            <Typography variant="caption" color="text.secondary">
              Saving keeps this card as well, since a grouping only holds cards
              you are keeping.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} disabled={save.isPending}>
          {justLiked ? 'Not now' : 'Cancel'}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={save.isPending}
          onClick={() => save.mutate({ key: card.key, kept: card.kept !== false })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
